import { useCallback, useEffect, useState } from 'react';
import { useMutation, useSubscription } from '@apollo/client';
import gql from 'graphql-tag';
import { ulid } from 'ulid';

/**
 * Starts an AOR preview and streams its live output, or follows one core-resources started (#33713).
 *
 * Not `useBulkTask`: that resolves the task name from the route via `useToolName`, which only works on
 * a registered tool page. `taskRunId` is minted client-side per the existing tools contract.
 */
const START_BULK_TASK = gql`
  mutation startBulkTask($task: String!, $taskParams: AWSJSON!) {
    startBulkTask: toolsStartBulkTask(task: $task, taskParams: $taskParams) {
      taskRunId
    }
  }
`;

const TASK_LIVE_OUTPUT_SUBSCRIPTION = gql`
  subscription onTaskLiveOutput($taskRunId: String!) {
    onTaskLiveOutput(taskRunId: $taskRunId) {
      taskRunId
      message
      type
    }
  }
`;

const TASK_NAME = 'aorPreview';

/** Comfortably longer than a cold enumerate's first sendLiveMessage, which follows an SSM read. */
const ATTACH_SILENCE_TIMEOUT_MS = 45000;

export const useAorPreview = ({ onComplete } = {}) => {
  const [taskRunId, setTaskRunId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filePaths, setFilePaths] = useState([]);
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  // Only set by attach(): start() subscribes before the run begins, so it has no silence window.
  const [attachedAt, setAttachedAt] = useState(null);
  const [startBulkTask] = useMutation(START_BULK_TASK);

  useSubscription(TASK_LIVE_OUTPUT_SUBSCRIPTION, {
    variables: { taskRunId },
    skip: !taskRunId,
    onData: ({ data }) => {
      const payload = data?.data?.onTaskLiveOutput;
      if (!payload) {
        return;
      }

      // MESSAGE (or no type) is progress; OUTPUT and ERROR are terminal.
      if (!payload.type || payload.type === 'MESSAGE') {
        setMessages((prev) => [...prev, payload.message]);
        return;
      }

      setIsRunning(false);
      if (payload.type === 'ERROR') {
        setError(payload.message);
        return;
      }
      if (payload.type === 'OUTPUT') {
        // The snapshot CSV links ride on the OUTPUT payload — surface them, or the artifacts the
        // operator is meant to review before approving are unreachable.
        try {
          const parsed = JSON.parse(payload.message);
          setFilePaths(parsed?.filePaths ?? []);
        } catch {
          setFilePaths([]);
        }
        onComplete?.(payload.message);
      }
    }
  });

  const start = useCallback(
    async (aorRequestId) => {
      const newTaskRunId = ulid();
      setTaskRunId(newTaskRunId);
      setMessages([]);
      setError(null);
      setIsRunning(true);

      try {
        await startBulkTask({
          variables: {
            task: TASK_NAME,
            taskParams: JSON.stringify({ taskRunId: newTaskRunId, aorRequestId })
          }
        });
      } catch (e) {
        setIsRunning(false);
        setError(e.message);
      }

      return newTaskRunId;
    },
    [startBulkTask]
  );

  /** The run is already going, so only the subscription is left to establish. */
  const attach = useCallback((serverTaskRunId) => {
    setTaskRunId(serverTaskRunId);
    setMessages([]);
    setFilePaths([]);
    setError(null);
    setIsRunning(true);
    setAttachedAt(Date.now());
  }, []);

  // An attached run started before this subscription existed, and AppSync does not replay. A run that
  // fails fast can publish its only message into that gap, leaving the dialog claiming it is running
  // forever. Say so instead, and point at the surface that does have the answer.
  useEffect(() => {
    if (!attachedAt || !isRunning || messages.length) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setError('No progress received. Check the AOR Runs tab for this request — the preview may have failed.');
      setIsRunning(false);
    }, ATTACH_SILENCE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [attachedAt, isRunning, messages.length]);

  const reset = useCallback(() => {
    setTaskRunId(null);
    setMessages([]);
    setFilePaths([]);
    setError(null);
    setIsRunning(false);
    setAttachedAt(null);
  }, []);

  return { start, attach, reset, taskRunId, messages, filePaths, error, isRunning };
};
